import { useState, useEffect } from "react";
import GameLayout from "../../components/GameLayout";
import API from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { ShieldCheck, ShieldAlert, KeyRound, Copy, RefreshCw, Trash2, Download, Eye, EyeOff } from "lucide-react";

export default function AdminSecurity() {
  const [status, setStatus] = useState({ enabled: false, backup_codes_remaining: 0 });
  const [loading, setLoading] = useState(true);

  // Setup flow
  const [setupData, setSetupData] = useState(null); // {secret, qr_data_url, otpauth_uri}
  const [setupCode, setSetupCode] = useState("");
  const [setupBusy, setSetupBusy] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null); // shown once

  // Disable flow
  const [disablePw, setDisablePw] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [disableBusy, setDisableBusy] = useState(false);

  // Regenerate
  const [regenCode, setRegenCode] = useState("");
  const [regenBusy, setRegenBusy] = useState(false);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/auth/2fa/status");
      setStatus(data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { loadStatus(); }, []);

  const startSetup = async () => {
    setSetupBusy(true);
    try {
      const { data } = await API.post("/auth/2fa/setup");
      setSetupData(data);
      setBackupCodes(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start 2FA setup");
    } finally {
      setSetupBusy(false);
    }
  };

  const verifySetup = async (e) => {
    e?.preventDefault?.();
    if (!setupCode.trim()) return toast.error("Enter the 6-digit code from your authenticator");
    setSetupBusy(true);
    try {
      const { data } = await API.post("/auth/2fa/verify-setup", { code: setupCode.trim() });
      setBackupCodes(data.backup_codes || []);
      setSetupData(null);
      setSetupCode("");
      await loadStatus();
      toast.success("2FA enabled. Save your backup codes!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally {
      setSetupBusy(false);
    }
  };

  const disable2FA = async (e) => {
    e?.preventDefault?.();
    setDisableBusy(true);
    try {
      await API.post("/auth/2fa/disable", { password: disablePw, code: disableCode.trim() });
      setDisablePw(""); setDisableCode("");
      setBackupCodes(null);
      await loadStatus();
      toast.success("2FA disabled");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to disable 2FA");
    } finally {
      setDisableBusy(false);
    }
  };

  const regenerate = async (e) => {
    e?.preventDefault?.();
    setRegenBusy(true);
    try {
      const { data } = await API.post("/auth/2fa/regenerate-backup-codes", { code: regenCode.trim() });
      setBackupCodes(data.backup_codes || []);
      setRegenCode("");
      await loadStatus();
      toast.success("New backup codes generated. Previous codes are invalid.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to regenerate");
    } finally {
      setRegenBusy(false);
    }
  };

  const copyAll = (codes) => {
    const text = codes.join("\n");
    navigator.clipboard?.writeText(text);
    toast.success("Copied to clipboard");
  };

  const downloadCodes = (codes) => {
    const blob = new Blob([`CrystoBag Admin 2FA Backup Codes\nGenerated: ${new Date().toISOString()}\n\n${codes.join("\n")}\n\nEach code can be used only once.`], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "crystobag-2fa-backup-codes.txt"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <GameLayout title="Admin Security">
      <div className="max-w-3xl space-y-6" data-testid="admin-security-page">
        {/* Status card */}
        <div className="bg-[#131722] rounded-xl border border-[#2A3241] p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${status.enabled ? "bg-[#10B981]/10" : "bg-[#F59E0B]/10"}`}>
                {status.enabled
                  ? <ShieldCheck className="w-6 h-6 text-[#10B981]" />
                  : <ShieldAlert className="w-6 h-6 text-[#F59E0B]" />}
              </div>
              <div>
                <h2 className="text-lg font-bold text-[#F8FAFC]">Two-Factor Authentication (TOTP)</h2>
                <p className="text-sm text-[#94A3B8] mt-1">
                  Protect your admin account with a time-based 6-digit code from Google Authenticator, Authy or 1Password.
                </p>
                {loading ? (
                  <p className="text-xs text-[#64748B] mt-3 font-mono-game">CHECKING…</p>
                ) : status.enabled ? (
                  <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-md bg-[#10B981]/10 border border-[#10B981]/30 text-xs font-bold text-[#10B981] font-mono-game" data-testid="twofa-status-enabled">
                    <ShieldCheck className="w-3.5 h-3.5" /> ENABLED · {status.backup_codes_remaining} backup codes left
                  </div>
                ) : (
                  <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-md bg-[#F59E0B]/10 border border-[#F59E0B]/30 text-xs font-bold text-[#F59E0B] font-mono-game" data-testid="twofa-status-disabled">
                    <ShieldAlert className="w-3.5 h-3.5" /> NOT ENABLED
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Backup codes display (one-time) */}
        {backupCodes && (
          <div className="bg-[#131722] rounded-xl border border-[#FFD600]/40 p-6" data-testid="backup-codes-panel">
            <div className="flex items-start gap-3 mb-4">
              <KeyRound className="w-5 h-5 text-[#FFD600] shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base font-bold text-[#FFD600]">Save your backup codes</h3>
                <p className="text-xs text-[#94A3B8] mt-1">
                  Each code can be used <b>once</b> if you lose access to your authenticator app. They will not be shown again.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {backupCodes.map((c, i) => (
                <div key={i} className="font-mono-game text-sm text-[#F8FAFC] bg-[#0B0E14] border border-[#2A3241] rounded px-3 py-2 tracking-widest" data-testid={`backup-code-${i}`}>{c}</div>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => copyAll(backupCodes)} data-testid="copy-backup-codes-btn" className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-widest rounded-md border border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/10">
                <Copy className="w-3.5 h-3.5" /> Copy
              </button>
              <button onClick={() => downloadCodes(backupCodes)} data-testid="download-backup-codes-btn" className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-widest rounded-md border border-[#94A3B8]/30 text-[#94A3B8] hover:bg-[#1C212E]">
                <Download className="w-3.5 h-3.5" /> Download
              </button>
              <button onClick={() => setBackupCodes(null)} className="ml-auto px-3 py-2 text-xs font-bold uppercase tracking-widest rounded-md text-[#64748B] hover:text-[#94A3B8]">
                I&apos;ve saved them
              </button>
            </div>
          </div>
        )}

        {/* Setup flow */}
        {!status.enabled && !backupCodes && (
          <div className="bg-[#131722] rounded-xl border border-[#2A3241] p-6">
            <h3 className="text-base font-bold text-[#F8FAFC] mb-1">Enable 2FA</h3>
            <p className="text-sm text-[#94A3B8] mb-4">Scan the QR code in Google Authenticator, then enter the 6-digit code to activate.</p>

            {!setupData ? (
              <button onClick={startSetup} disabled={setupBusy} data-testid="start-2fa-setup-btn"
                className="px-5 py-3 text-sm font-bold uppercase tracking-widest bg-[#00F0FF] text-[#0B0E14] rounded-lg hover:bg-[#00F0FF]/90 disabled:opacity-50 shadow-[0_0_15px_rgba(0,240,255,0.4)]">
                {setupBusy ? "Generating…" : "Start 2FA Setup"}
              </button>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-col md:flex-row gap-6 items-start">
                  <div className="bg-white p-3 rounded-lg" data-testid="totp-qr">
                    <img src={setupData.qr_data_url} alt="TOTP QR code" className="w-48 h-48" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Label className="text-[#94A3B8] text-xs">Or enter this secret manually:</Label>
                    <div className="mt-1 flex items-center gap-2">
                      <code className="flex-1 font-mono-game text-xs text-[#00F0FF] bg-[#0B0E14] border border-[#2A3241] rounded px-3 py-2 break-all" data-testid="totp-secret">
                        {setupData.secret}
                      </code>
                      <button onClick={() => { navigator.clipboard?.writeText(setupData.secret); toast.success("Copied"); }}
                        className="p-2 rounded-md border border-[#2A3241] text-[#94A3B8] hover:text-[#00F0FF]">
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-xs text-[#64748B] mt-3">
                      Issuer: <span className="text-[#94A3B8]">{setupData.issuer}</span><br />
                      Account: <span className="text-[#94A3B8]">{setupData.label}</span>
                    </p>
                  </div>
                </div>
                <form onSubmit={verifySetup} className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label className="text-[#94A3B8] text-sm mb-1 block">6-digit code</Label>
                    <Input value={setupCode} onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="123456" inputMode="numeric" autoFocus
                      className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] font-mono-game text-lg tracking-widest" data-testid="totp-verify-code-input" />
                  </div>
                  <button type="submit" disabled={setupBusy || setupCode.length < 6} data-testid="verify-2fa-setup-btn"
                    className="px-5 py-2.5 text-sm font-bold uppercase tracking-widest bg-[#10B981] text-[#0B0E14] rounded-lg hover:bg-[#10B981]/90 disabled:opacity-50">
                    {setupBusy ? "Verifying…" : "Verify & Enable"}
                  </button>
                </form>
                <button onClick={() => { setSetupData(null); setSetupCode(""); }} className="text-xs text-[#64748B] hover:text-[#94A3B8]">
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}

        {/* Regenerate backup codes */}
        {status.enabled && (
          <div className="bg-[#131722] rounded-xl border border-[#2A3241] p-6">
            <div className="flex items-start gap-3 mb-4">
              <RefreshCw className="w-5 h-5 text-[#00F0FF] mt-0.5" />
              <div>
                <h3 className="text-base font-bold text-[#F8FAFC]">Regenerate backup codes</h3>
                <p className="text-sm text-[#94A3B8]">Invalidates existing backup codes and creates 8 new ones.</p>
              </div>
            </div>
            <form onSubmit={regenerate} className="flex gap-2 items-end">
              <div className="flex-1">
                <Label className="text-[#94A3B8] text-sm mb-1 block">Current 6-digit code</Label>
                <Input value={regenCode} onChange={(e) => setRegenCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="123456" inputMode="numeric"
                  className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] font-mono-game tracking-widest" data-testid="regen-code-input" />
              </div>
              <button type="submit" disabled={regenBusy || regenCode.length < 6} data-testid="regen-backup-codes-btn"
                className="px-5 py-2.5 text-sm font-bold uppercase tracking-widest bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/40 rounded-lg hover:bg-[#00F0FF]/20 disabled:opacity-50">
                {regenBusy ? "…" : "Regenerate"}
              </button>
            </form>
          </div>
        )}

        {/* Disable */}
        {status.enabled && (
          <div className="bg-[#131722] rounded-xl border border-[#EF4444]/30 p-6">
            <div className="flex items-start gap-3 mb-4">
              <Trash2 className="w-5 h-5 text-[#EF4444] mt-0.5" />
              <div>
                <h3 className="text-base font-bold text-[#EF4444]">Disable 2FA</h3>
                <p className="text-sm text-[#94A3B8]">Weakens admin security. Requires password and a current 2FA code (or backup code).</p>
              </div>
            </div>
            <form onSubmit={disable2FA} className="space-y-3">
              <div>
                <Label className="text-[#94A3B8] text-sm mb-1 block">Current password</Label>
                <div className="relative">
                  <Input type={showPw ? "text" : "password"} value={disablePw} onChange={(e) => setDisablePw(e.target.value)} required
                    className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] pr-10" data-testid="disable-pw-input" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#94A3B8]">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-[#94A3B8] text-sm mb-1 block">2FA code (or backup code)</Label>
                <Input value={disableCode} onChange={(e) => setDisableCode(e.target.value)} placeholder="123456 or XXXX-XXXX" required
                  className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] font-mono-game tracking-widest" data-testid="disable-code-input" />
              </div>
              <button type="submit" disabled={disableBusy} data-testid="disable-2fa-btn"
                className="px-5 py-2.5 text-sm font-bold uppercase tracking-widest bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/40 rounded-lg hover:bg-[#EF4444]/20 disabled:opacity-50">
                {disableBusy ? "Disabling…" : "Disable 2FA"}
              </button>
            </form>
          </div>
        )}
      </div>
    </GameLayout>
  );
}
